'use client'

import { ElectionMetaResponseVo } from '@schemas/non-auth'

import { pxToRem } from '@ui/style/muiTheme'
import { Box } from '@mui/joy'
import UIText from '@components/ui/UIText'
import color from '@ui/style/color.theme'
import UIFlex from '@components/ui/UIFlex'
import DimdLayer from './DimdLayer'

interface ResultBannerProps {
	data: ElectionMetaResponseVo
	viewPoint: string
}

const ResultBanner = ({ data, viewPoint }: ResultBannerProps) => {
	const candidateImageMap: Record<string, { mo: string; pc: string }> = {
		이재명: {
			mo: 'url(/images/election/banner/candidate_elected_person1_bg_mo.jpg)',
			pc: 'url(/images/election/banner/candidate_elected_person1_bg_pc.jpg)',
		},
		김문수: {
			mo: 'url(/images/election/banner/candidate_elected_person2_bg_mo.jpg)',
			pc: 'url(/images/election/banner/candidate_elected_person2_bg_pc.jpg)',
		},
		이준석: {
			mo: 'url(/images/election/banner/candidate_elected_person3_bg_mo.jpg)',
			pc: 'url(/images/election/banner/candidate_elected_person3_bg_pc.jpg)',
		},
	}

	const getCandidateImage = (candidateName: string) => candidateImageMap[candidateName]

	const candidateImage = getCandidateImage(data.topBannerChart.electionResult.candidateName)

	const getResultData = () => {
		const { isError, politicalPartyName, candidateName, resultRate, resultCount, gapCount } =
			data.topBannerChart.electionResult
		if (isError) {
			return {
				color: '#2E6782',
				politicalPartyName: '-',
				candidateName: '-',
				resultRate: '00.00',
				resultCount: '0',
				gapCount: '0',
			}
		}
		return {
			politicalPartyName,
			candidateName,
			resultRate,
			resultCount,
			gapCount,
		}
	}

	return (
		<>
			{data.topBannerChart.electionResult.isError && <DimdLayer />}

			<Box
				sx={{
					width: '100%',
					height: '100%',
					position: 'relative',
					backgroundImage: {
						xs: data.topBannerChart.electionResult.isError
							? 'url(/images/election/banner/default_elected_bg_mo.jpg)'
							: candidateImage.mo,
						md: data.topBannerChart.electionResult.isError
							? 'url(/images/election/banner/default_elected_bg_pc.jpg)'
							: candidateImage.pc,
					},
					backgroundSize: {
						xs: viewPoint === 'article' ? pxToRem(1800) : 'cover',
						md: 'cover',
					},
					backgroundPosition: {
						xs: viewPoint === 'article' ? 'center -10px' : 'center',
						md: 'center',
					},
					backgroundRepeat: 'no-repeat',
				}}
			>
				{!data.topBannerChart.electionResult.isError && (
					<Box
						sx={{
							width: {
								xs: pxToRem(68),
								md: pxToRem(100),
								lg: pxToRem(146),
							},
							height: {
								xs: pxToRem(59),
								md: pxToRem(87),
								lg: pxToRem(128),
							},
							position: 'absolute',
							left: {
								xs: 'inherit',
								md: '50%',
							},
							right: {
								xs: pxToRem(20),
								md: 'inherit',
							},
							top: {
								xs: pxToRem(20),
								md: pxToRem(81),
								lg: pxToRem(88),
								xl: pxToRem(103),
							},
							transform: {
								xs: 'inherit',
								md: 'translateX(-370px)',
								lg: 'translateX(-470px)',
								xl: 'translateX(-520px)',
							},
							backgroundImage: 'url(/images/election/banner/badge_elected_big.png)',
							backgroundSize: 'cover',
						}}
					/>
				)}

				<UIFlex
					flexDirection="column"
					alignItems={{
						xs: 'flex-end',
						md: 'flex-start',
					}}
					justifyContent="center"
					gap={{
						xs: viewPoint === 'article' ? pxToRem(6) : pxToRem(12),
						md: pxToRem(20),
						lg: pxToRem(28),
					}}
					sx={{
						position: 'absolute',
						width: {
							xs: pxToRem(164),
							md: pxToRem(317),
							lg: pxToRem(403),
						},
						top: {
							xs: viewPoint === 'article' ? pxToRem(92) : pxToRem(98),
							md: pxToRem(58),
							lg: pxToRem(52),
							xl: pxToRem(77),
						},
						left: {
							xs: 'inherit',
							lg: '50%',
						},
						right: {
							xs: pxToRem(20),
							md: pxToRem(24),
							lg: 'inherit',
						},
						marginLeft: {
							xs: 'inherit',
							lg: pxToRem(80),
							xl: pxToRem(145),
						},
					}}
				>
					<UIFlex
						flexDirection="column"
						alignItems="center"
						gap={{
							xs: pxToRem(8),
							lg: pxToRem(12),
						}}
						sx={{
							width: '100%',
							position: 'relative',
						}}
					>
						<UIText
							sx={{
								width: '100%',
								textAlign: 'center',
								position: 'relative',
								fontSize: {
									xs: pxToRem(16),
									md: pxToRem(20),
									lg: pxToRem(24),
								},
								lineHeight: {
									xs: viewPoint === 'article' ? pxToRem(16) : pxToRem(24),
									md: pxToRem(20),
									lg: pxToRem(24),
								},
								fontWeight: '700',
								color: '#FFDE24',
								'&:before': {
									content: '""',
									position: 'absolute',
									top: '50%',
									left: 0,
									transform: 'translateY(-50%)',
									width: {
										xs: pxToRem(23),
										md: pxToRem(76),
										lg: pxToRem(96),
									},
									height: pxToRem(1),
									backgroundColor: '#FFDE24',
									opacity: 0.4,
								},
								'&:after': {
									content: '""',
									position: 'absolute',
									top: '50%',
									right: 0,
									transform: 'translateY(-50%)',
									width: {
										xs: pxToRem(23),
										md: pxToRem(76),
										lg: pxToRem(96),
									},
									height: pxToRem(1),
									backgroundColor: '#FFDE24',
									opacity: 0.4,
								},
							}}
						>
							제 21대 대통령
						</UIText>
						<UIFlex
							flexDirection="column"
							alignItems="center"
							gap={{
								xs: pxToRem(8),
								lg: pxToRem(10),
							}}
						>
							<UIText
								sx={{
									fontSize: {
										xs: pxToRem(32),
										md: pxToRem(36),
										lg: pxToRem(56),
									},
									lineHeight: {
										xs: pxToRem(32),
										md: pxToRem(36),
										lg: pxToRem(56),
									},
									fontWeight: '700',
									color: color.colWhite,
								}}
							>
								{getResultData().candidateName} 당선
							</UIText>
							<UIText
								sx={{
									fontSize: {
										xs: pxToRem(14),
										md: pxToRem(16),
										lg: pxToRem(20),
									},
									lineHeight: {
										xs: pxToRem(14),
										md: pxToRem(16),
										lg: pxToRem(20),
									},
									color: color.colWhite,
									opacity: 0.8,
								}}
							>
								{getResultData().politicalPartyName}
							</UIText>
						</UIFlex>
						<Box
							sx={{
								width: '100%',
								height: pxToRem(1),
								backgroundColor: '#FFDE24',
								opacity: 0.4,
							}}
						/>
					</UIFlex>
					<UIFlex
						flexDirection={{
							xs: 'column',
							md: 'row',
						}}
						alignItems={{
							xs: 'flex-end',
							md: 'center',
						}}
						gap={{
							xs: pxToRem(8),
							lg: pxToRem(12),
						}}
						sx={{
							width: '100%',
						}}
					>
						<UIFlex flexDirection="row" alignItems="center" gap={pxToRem(8)}>
							<UIText
								sx={{
									fontSize: {
										xs: pxToRem(14),
										lg: pxToRem(18),
									},
									lineHeight: {
										xs: pxToRem(14),
										lg: pxToRem(18),
									},
									fontWeight: '600',
									color: '#FFDE24',
								}}
							>
								득표율
							</UIText>
							<UIText
								sx={{
									fontSize: {
										xs: pxToRem(33),
										lg: pxToRem(50),
									},
									lineHeight: {
										xs: pxToRem(33),
										lg: pxToRem(50),
									},
									fontWeight: '700',
									color: '#FFDE24',
								}}
							>
								{getResultData().resultRate}
								<UIText
									sx={{
										fontSize: {
											xs: pxToRem(16),
											lg: pxToRem(24),
										},
										lineHeight: {
											xs: pxToRem(21),
											lg: pxToRem(30),
										},
										color: '#FFDE24',
										fontFeatureSettings: 'case',
									}}
								>
									%
								</UIText>
							</UIText>
						</UIFlex>
						<Box
							sx={{
								width: pxToRem(1),
								height: {
									md: pxToRem(32),
									lg: pxToRem(36),
								},
								backgroundColor: color.colWhite,
								opacity: 0.4,
								display: {
									xs: 'none',
									md: 'block',
								},
							}}
						/>
						<UIFlex
							flexDirection="column"
							alignItems={{
								xs: 'flex-end',
								md: 'flex-start',
							}}
							gap={pxToRem(8)}
							sx={{
								display: {
									xs: viewPoint === 'article' ? 'none' : 'flex',
									md: 'flex',
								},
							}}
						>
							<UIText
								sx={{
									fontSize: {
										xs: pxToRem(13),
										lg: pxToRem(15),
									},
									lineHeight: {
										xs: pxToRem(13),
										lg: pxToRem(15),
									},
									fontWeight: '700',
									color: color.colWhite,
									opacity: 0.7,
								}}
							>
								득표수 : {getResultData().resultCount}표
							</UIText>
							<UIText
								sx={{
									fontSize: {
										xs: pxToRem(13),
										lg: pxToRem(15),
									},
									lineHeight: {
										xs: pxToRem(13),
										lg: pxToRem(15),
									},
									fontWeight: '400',
									color: color.colWhite,
									opacity: 0.7,
								}}
							>
								2위와 표차 : {getResultData().gapCount}표
							</UIText>
						</UIFlex>
					</UIFlex>
					<UIFlex
						flexDirection="row"
						gap={pxToRem(2)}
						sx={{
							fontSize: pxToRem(14),
							lineHeight: pxToRem(14),
							color: color.colWhite,
							marginTop: {
								xs: pxToRem(8),
								md: pxToRem(0),
							},
						}}
					>
						개표결과 자세히 보기
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								marginTop: pxToRem(-1),
							}}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="16"
								height="16"
								viewBox="0 0 16 16"
								fill="none"
							>
								<path d="M6 12L10 8L6 4" stroke="#ffffff" strokeWidth="1.5" />
							</svg>
						</Box>
					</UIFlex>
				</UIFlex>
			</Box>
		</>
	)
}

export default ResultBanner
